// Core imports
import React, { useState, useEffect, useCallback, Fragment, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

// Component imports
import { Button, Space } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import ResponsiveTagsList from '../../UI/ResponsiveTagsList';

// Misc imports
import { changePrimaryLanguage, changeSelectedLanguage, updateMissingTranslationInfo } from '../../../store/knowledgeBases/knowledgeBasesActions';
import { LANGUAGE_OPTIONS } from '../../../constants/main';
import { ARTICLE_GROUP_IDS, KNOWLEDGE_BASE_URL_PARAMS } from '../../../constants/knowledgeBases';
import { KnowledgeBaseConfirmContext } from './KnowledgeBasesForm';
import { getCurrentQueryState } from '../../../store/utils/getCurrentQueryState';
import { useParams } from 'react-router-dom';
import { useFetchKnowledgeBaseQuery } from '../../../store/knowledgeBases/knowledgeBasesApi';

const KnowledgeBasesFormLanguageBar = () => {
  const { orgId, knowledgeBaseId } = useParams();
  const dispatch = useDispatch();
  const { primaryLanguage, selectedLanguage } = useSelector((state) => state.knowledgeBases);
  const { handleShowConfirm } = useContext(KnowledgeBaseConfirmContext);
  const { data: knowledgeBase } = useFetchKnowledgeBaseQuery({ orgId: orgId, knowledgeBaseId: knowledgeBaseId, urlParams: { includeTenants: true, includeIntegration: true } }, { skip: !knowledgeBaseId });
  const { data: articles } = getCurrentQueryState('fetchKnowledgeBaseArticles', { orgId, knowledgeBaseId, urlParams: KNOWLEDGE_BASE_URL_PARAMS.ARTICLES }, !knowledgeBaseId );
  const { data: groups } = getCurrentQueryState('fetchKnowledgeBaseGroups', { orgId, knowledgeBaseId, urlParams: KNOWLEDGE_BASE_URL_PARAMS.GROUPS }, !knowledgeBaseId );

  const [sortedLanguages, setSortedLanguages] = useState([]);
  const [animatingLanguages, setAnimatingLanguages] = useState(false);
  const [newLanguage, setNewLanguage] = useState(null);

  const [articlesWithMissingTranslation, setArticlesWithMissingTranslation] = useState([]);
  const [groupsWithMissingTranslation, setGroupsWithMissingTranslations] = useState([]);
  const [languagesWithMissingTranslation, setLanguagesWithMissingTranslations] = useState([]);

  useEffect(() => {
    if (knowledgeBase?.languages) {
      const primary = knowledgeBase?.languages.find(lang => lang.isPrimary)?.languageCode;
      if (primary) {
        dispatch(changePrimaryLanguage(primary));
        if (!selectedLanguage || !knowledgeBase?.languages.some(l => l.languageCode === selectedLanguage)) {
          dispatch(changeSelectedLanguage(primary));
        }
      }

      const sorted = [...knowledgeBase.languages].sort((a, b) => {
        if (a.isPrimary === b.isPrimary) {
          return 0;
        }
        return a.isPrimary ? -1 : 1;
      });
      setSortedLanguages(sorted);
    } else {
      setSortedLanguages([]);
    }
  }, [knowledgeBase, dispatch]);

  useEffect(() => {
    if (knowledgeBase?.languages && articles?.items && primaryLanguage) {
      const optionalLangs = knowledgeBase?.languages.filter(lang => !lang.isPrimary);
      const missing = [];
      articles.items.forEach(article => {
        const primaryContent = article.content.find(c => c.languageCode === primaryLanguage);
        optionalLangs.forEach(lang => {
          const optContent = article.content.find(c => c.languageCode === lang.languageCode);
          if (!optContent || !optContent.title || (primaryContent?.body?.length > 0 && optContent?.body?.length === 0)) {
            missing.push({ articleId: article.id, groupId: article.groupId, languageCode: lang.languageCode });
          }
        });
      });

      setArticlesWithMissingTranslation(missing);
    } else {
      setArticlesWithMissingTranslation([]);
    }
  }, [knowledgeBase, articles, primaryLanguage]);

  useEffect(() => {
    if (knowledgeBase?.languages && groups && primaryLanguage) {
      const optionalLangs = knowledgeBase?.languages.filter(lang => !lang.isPrimary);
      const missing = [];
      groups.forEach(group => {
        if (group.id === ARTICLE_GROUP_IDS.UNGROUPED) {
          return;
        }
        optionalLangs.forEach(lang => {
          if (!group.name || !group.name[lang.languageCode]) {
            missing.push({ groupId: group.id, languageCode: lang.languageCode });
          }
        });
      });
      setGroupsWithMissingTranslations(missing);
    } else {
      setGroupsWithMissingTranslations([]);
    }
  }, [knowledgeBase, groups, primaryLanguage]);

  useEffect(() => {
    if (knowledgeBase?.languages) {
      const optionalLangs = knowledgeBase?.languages.filter(lang => !lang.isPrimary);
      const missingLangCodes = optionalLangs
        .map(lang => {
          const artMissing = articlesWithMissingTranslation.some(a => a.languageCode === lang.languageCode);
          const grpMissing = groupsWithMissingTranslation.some(g => g.languageCode === lang.languageCode);
          return (artMissing || grpMissing) ? lang.languageCode : null;
        })
        .filter(Boolean);
      setLanguagesWithMissingTranslations(missingLangCodes);
      dispatch(updateMissingTranslationInfo({ articlesWithMissingTranslation, groupsWithMissingTranslation, languagesWithMissingTranslation: missingLangCodes }));
    } else {
      setLanguagesWithMissingTranslations([]);
    }
  }, [articlesWithMissingTranslation, groupsWithMissingTranslation, knowledgeBase]);

  const swapLanguagesInternal = useCallback((selectedLangCode, omittedLangs) => {
    if (omittedLangs?.some(lang => lang?.value === selectedLangCode)) {
      setNewLanguage(selectedLangCode);
      setAnimatingLanguages(true);
      setSortedLanguages(prev => {
        const index = prev.findIndex(lang => lang.languageCode === selectedLangCode);
        if (index === 1 || index === -1) {
          return prev;
        }
        let newLangs = [...prev];
        const langToMove = newLangs.splice(index, 1)[0];
        newLangs.splice(1, 0, langToMove);
        return newLangs;
      });
    }
  }, []);

  useEffect(() => {
    if (animatingLanguages) {
      const timer = setTimeout(() => {
        setAnimatingLanguages(false);
        setNewLanguage(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [animatingLanguages]);

  const handleLanguageButtonClick = (languageValue) => {
    handleShowConfirm(() => dispatch(changeSelectedLanguage(languageValue)));
  };

  const renderLanguageButtonInternal = (language, omittedLanguages = [], isOmmitedLanguagesArray = false) => {
    const isEntering = newLanguage === language.value;
    const isPrimary = primaryLanguage === language.value;
    const shiftClass = animatingLanguages && !isEntering && !isPrimary && !isOmmitedLanguagesArray ? 'language-shift' : '';
    const enterClass = isEntering ? 'language-enter' : '';
    const langOption = LANGUAGE_OPTIONS.find(opt => opt.key === language.value);

    return (
      <Button
        key={language.value}
        disabled={knowledgeBase?.languages.length === 1}
        onClick={() => {
          swapLanguagesInternal(language.value, omittedLanguages);
          handleLanguageButtonClick(language.value);
        }}
        type="secondary"
        className={classNames('fw-400 m-1 text-color-default', {
          'border-color-primary': selectedLanguage === language.value,
          'border-color-white': selectedLanguage !== language.value,
          [shiftClass]: shiftClass,
          [enterClass]: enterClass
        })}
        shape="round"
      >
        <Space>
          {languagesWithMissingTranslation.includes(language.value) && <ExclamationCircleOutlined className="text-error"/>}
          {langOption?.icon}
          {langOption?.label}
        </Space>
      </Button>
    );
  };

  if (!knowledgeBase?.languages || sortedLanguages.length === 0) {
    return null;
  }

  return (
    <ResponsiveTagsList
      values={
        sortedLanguages.map(lang => ({
          icon: LANGUAGE_OPTIONS.find(opt => opt.key === lang.languageCode)?.icon,
          label: LANGUAGE_OPTIONS.find(opt => opt.key === lang.languageCode)?.label,
          value: lang.languageCode
        }))
      }
      omittedButtonClassName={'cursor-pointer'}
      tagRender={renderLanguageButtonInternal}
      renderOmittedValues={(omittedLangs) => (
        <Fragment>
          {omittedLangs
            .sort((a, b) => a.label.localeCompare(b.label))
            .map(lang => renderLanguageButtonInternal(lang, omittedLangs, true))}
        </Fragment>
      )}
    />
  );
};

export default KnowledgeBasesFormLanguageBar;
